import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import { FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import httpService from "../../services/httpService";

const PreviewRow = ({ label, value }) => (
    <div className="d-flex justify-content-between py-1 border-bottom">
        <span className="text-muted">{label}</span>
        <Badge bg="secondary">{value ?? 0}</Badge>
    </div>
);

const PodcasterCancelPodcastClub = ({ clubGuid }) => {
    const navigate = useNavigate();

    const [showModal, setShowModal]           = useState(false);
    const [preview, setPreview]               = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmText, setConfirmText]       = useState('');
    const [notifyListeners, setNotifyListeners] = useState(true);
    const [deleting, setDeleting]             = useState(false);

    const openModal = () => {
        setShowModal(true);
        setConfirmText('');
        setPreview(null);
        setPreviewLoading(true);
        httpService.get(`/dash/club/${clubGuid}/delete-preview`)
            .then((res) => setPreview(res.data))
            .catch(() => {})
            .finally(() => setPreviewLoading(false));
    };

    const closeModal = () => {
        if (deleting) return;
        setShowModal(false);
        setConfirmText('');
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await httpService.delete(`/dash/club/${clubGuid}`, {
                data: {
                    confirmation: confirmText,
                    send_email_notifications: notifyListeners,
                },
            });
            navigate('/podcaster-dashboard');
        } catch {
        } finally {
            setDeleting(false);
        }
    };

    const canDelete = confirmText === 'delete me' && !deleting;

    return (
        <>
            <Row>
                <Col sm={12} className="mb-4">
                    <img
                        src="/img/delete-wide.jpg"
                        alt="Delete Podcast Club"
                        style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                </Col>
                <Col sm={12}>
                    <h4>Delete this Podcast Club</h4>
                    <p className="text-muted">
                        Permanently deletes this Podcast Club and all related data — episodes, listeners, skills, discussions, invites, and insights. This cannot be undone.
                    </p>
                    <p className="text-muted">
                        If you are unsure, consider pausing invites or archiving the club instead.
                    </p>
                    <Button variant="danger" onClick={openModal}>
                        Delete this Podcast Club
                    </Button>
                </Col>
            </Row>

            <Modal show={showModal} onHide={closeModal} size="lg" backdrop="static" keyboard={false}>
                <Modal.Body className="p-0">
                    <img
                        src="/img/delete-wide.jpg"
                        alt="Delete"
                        style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: 'var(--bs-modal-inner-border-radius) var(--bs-modal-inner-border-radius) 0 0' }}
                    />
                    <div className="p-4">

                        {/* Warning banner */}
                        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4">
                            <FaExclamationTriangle />
                            <span><strong>This action is permanent and cannot be undone.</strong> All data will be deleted immediately.</span>
                        </div>

                        {/* Deletion preview */}
                        {previewLoading && (
                            <div className="text-center py-3 text-muted">
                                <FaSpinner className="me-2" />Loading deletion summary…
                            </div>
                        )}

                        {preview && !previewLoading && (
                            <div className="mb-4">
                                <h6 className="mb-3">The following will be permanently deleted for <strong>{preview.club_title}</strong>:</h6>
                                <PreviewRow label="Listeners / members"  value={preview.listeners_count} />
                                <PreviewRow label="Episodes"             value={preview.episodes_count} />
                                <PreviewRow label="Episode pivots"       value={preview.episode_pivots_count} />
                                <PreviewRow label="Key takeaways"        value={preview.key_takeaways_count} />
                                <PreviewRow label="Practices"            value={preview.practices_count} />
                                <PreviewRow label="Skills"               value={preview.skills_count} />
                                <PreviewRow label="Discussions"          value={preview.discussions_count} />
                                <PreviewRow label="Comments"             value={preview.comments_count} />
                                <PreviewRow label="Invites"              value={preview.invites_count} />
                                <PreviewRow label="Access codes"         value={preview.access_codes_count} />
                            </div>
                        )}

                        {/* Email notification toggle */}
                        <div className="mb-4 p-3 bg-light rounded">
                            <Form.Check
                                type="switch"
                                id="notify-listeners-switch"
                                label={<strong>Notify listeners by email</strong>}
                                checked={notifyListeners}
                                onChange={(e) => setNotifyListeners(e.target.checked)}
                            />
                            <Form.Text className="text-muted d-block mt-1">
                                {notifyListeners
                                    ? 'The club owner and all listeners will receive a cancellation email.'
                                    : 'The club will be deleted silently. Only the club owner will be notified.'}
                            </Form.Text>
                        </div>

                        {/* Confirmation input */}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Type <code>delete me</code> to confirm deletion
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="delete me"
                                disabled={deleting}
                                isValid={confirmText === 'delete me'}
                                isInvalid={confirmText.length > 0 && confirmText !== 'delete me'}
                            />
                            <Form.Control.Feedback type="invalid">
                                Must be exactly: delete me
                            </Form.Control.Feedback>
                        </Form.Group>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={!canDelete}>
                        {deleting
                            ? <><FaSpinner className="me-2" />Deleting…</>
                            : 'Permanently Delete Club'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default PodcasterCancelPodcastClub;
